// src/components/AssetBuilder.jsx
import { useState, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';
import { getAuth } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: '📝' },
  { value: 'number', label: 'Number', icon: '🔢' },
  { value: 'date', label: 'Date', icon: '📅' },
  { value: 'file', label: 'File', icon: '📎' },
];

const ASSET_TYPES = [
  'Person',
  'Vehicle',
  'Equipment',
  'Tool',
  'Location',
  'Document',
  'Other'
];

export default function AssetBuilder({ companyId, asset, onSave, onCancel }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(asset?.title || '');
  const [type, setType] = useState(asset?.type || '');
  const [customType, setCustomType] = useState('');
  const [role, setRole] = useState(asset?.role || '');
  const [mainPhotoURL, setMainPhotoURL] = useState(asset?.mainPhotoURL || '');
  const [fields, setFields] = useState(asset?.fields || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalUrl, setImageModalUrl] = useState('');
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [fileModalUrl, setFileModalUrl] = useState('');
  const [fileModalName, setFileModalName] = useState('');

  const auth = getAuth();

  function addField(fieldType) {
    const newField = {
      id: `field_${Date.now()}`,
      type: fieldType,
      label: '',
      value: '',
      showOnMobile: false,
      // For date fields
      enableExpiry: false,
      expiryReminders: []
    };

    setFields([...fields, newField]);
  }

  function updateField(fieldId, updates) {
    setFields(fields.map(f => 
      f.id === fieldId ? { ...f, ...updates } : f
    ));
  }

  function deleteField(fieldId) {
    const field = fields.find(f => f.id === fieldId);
    
    // Delete file from storage if it's a file field
    if (field?.type === 'file' && field?.value) {
      try {
        const fileRef = ref(storage, field.value);
        deleteObject(fileRef).catch(err => console.warn('Could not delete file:', err));
      } catch (err) {
        console.warn('Error deleting file:', err);
      }
    }

    setFields(fields.filter(f => f.id !== fieldId));
  }

  function moveField(index, direction) {
    const newFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newFields.length) return;
    
    [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
    setFields(newFields);
  }

  async function handleMainPhotoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert(t('assets.selectImageFile'));
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('assets.imageTooLarge'));
      return;
    }

    setUploading(true);

    try {
      // Delete old photo if exists
      if (mainPhotoURL) {
        try {
          const oldRef = ref(storage, mainPhotoURL);
          await deleteObject(oldRef);
        } catch (err) {
          console.warn('Could not delete old photo:', err);
        }
      }

      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `companies/${companyId}/assets/photos/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      setMainPhotoURL(downloadURL);
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert(t('assets.failedToUploadPhoto') + ': ' + error.message);
    } finally {
      setUploading(false);
    }
  }

  async function handleFileFieldUpload(fieldId, file) {
    if (!file) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      alert(t('assets.fileTooLarge'));
      return;
    }

    try {
      const field = fields.find(f => f.id === fieldId);
      
      // Delete old file if exists
      if (field?.value) {
        try {
          const oldRef = ref(storage, field.value);
          await deleteObject(oldRef);
        } catch (err) {
          console.warn('Could not delete old file:', err);
        }
      }

      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `companies/${companyId}/assets/files/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      updateField(fieldId, { value: downloadURL, fileName: file.name });
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(t('assets.failedToUploadFile') + ': ' + error.message);
    }
  }

  function toggleReminder(fieldId, reminder) {
    const field = fields.find(f => f.id === fieldId);
    if (!field) return;

    const reminders = field.expiryReminders || [];
    const index = reminders.indexOf(reminder);

    if (index >= 0) {
      // Remove reminder
      updateField(fieldId, {
        expiryReminders: reminders.filter(r => r !== reminder)
      });
    } else {
      // Add reminder
      updateField(fieldId, {
        expiryReminders: [...reminders, reminder]
      });
    }
  }

  async function handleSave() {
    if (!title.trim()) {
      alert(t('assets.enterAssetTitle'));
      return;
    }

    const finalType = type === 'custom' ? customType : type;

    if (!finalType.trim()) {
      alert(t('assets.selectAssetType'));
      return;
    }

    setSaving(true);

    try {
      const assetData = {
        title: title.trim(),
        type: finalType.trim(),
        role: role.trim(),
        mainPhotoURL,
        fields
      };

      await onSave(assetData);
    } catch (error) {
      console.error('Error saving asset:', error);
      alert('Failed to save asset: ' + error.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="relative h-full flex flex-col bg-gradient-to-br from-slate-900 via-slate-950 to-rose-900/70 text-white overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.08),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.06),transparent_30%)] pointer-events-none" />
      <div className="relative z-10 flex flex-col h-full">
        {/* Header */}
        <div className="bg-white/10 border-b border-white/15 px-6 py-4 backdrop-blur-xl shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">
                {asset ? '✏️ ' + t('assets.editAsset') : '➕ ' + t('assets.newAsset')}
              </h1>
              <p className="text-sm text-white/70 mt-1">
                {t('assets.configureDetails')}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={onCancel}
                disabled={saving}
                className="px-4 py-2 border border-white/25 bg-white/10 text-white rounded-md hover:bg-white/20 backdrop-blur-md disabled:opacity-50"
              >
                {t('assets.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving || uploading}
                className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500 text-slate-900 px-4 py-2 rounded-md shadow-lg font-semibold hover:from-emerald-300 hover:via-cyan-300 hover:to-blue-400 disabled:opacity-60"
              >
                {saving ? t('assets.saving') : t('assets.saveAsset')}
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="max-w-5xl mx-auto p-6 space-y-6">
            
            {/* Basic Info Card */}
            <div className="bg-white/10 rounded-2xl border border-white/15 p-6 backdrop-blur-xl shadow-2xl">
            <h2 className="text-lg font-bold text-white mb-4">{t('assets.basicInformation')}</h2>

            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">
                  {t('assets.titleLabel')} <span className="text-rose-200">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('assets.titlePlaceholder')}
                  className="w-full border border-white/20 rounded-md px-3 py-2 bg-white/10 text-white placeholder-white/60 focus:outline-none focus:border-white/40 focus:ring-2 focus:ring-white/30"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">
                  {t('assets.type')} <span className="text-rose-200">*</span>
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full border border-white/20 rounded-md px-3 py-2 bg-white/10 text-white focus:outline-none focus:border-white/40 focus:ring-2 focus:ring-white/30"
                >
                  <option value="" className="bg-slate-900 text-white">{t('assets.selectType')}</option>
                  {ASSET_TYPES.map(assetType => (
                    <option key={assetType} value={assetType} className="bg-slate-900 text-white">{t(`assets.type${assetType}`)}</option>
                  ))}
                  <option value="custom" className="bg-slate-900 text-white">{t('assets.customOption')}</option>
                </select>

                {type === 'custom' && (
                  <input
                    type="text"
                    value={customType}
                    onChange={(e) => setCustomType(e.target.value)}
                    placeholder={t('assets.enterCustomType')}
                    className="w-full border border-white/20 rounded-md px-3 py-2 bg-white/10 text-white placeholder-white/60 focus:outline-none focus:border-white/40 focus:ring-2 focus:ring-white/30 mt-2"
                  />
                )}
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">
                  {t('assets.roleCategory')}
                </label>
                <input
                  type="text"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder={t('assets.rolePlaceholder')}
                  className="w-full border border-white/20 rounded-md px-3 py-2 bg-white/10 text-white placeholder-white/60 focus:outline-none focus:border-white/40 focus:ring-2 focus:ring-white/30"
                />
              </div>

              {/* Main Photo */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">
                  {t('assets.mainPhoto')}
                </label>
                
                {mainPhotoURL ? (
                  <div className="relative w-48 h-48 border-2 border-white/25 rounded-xl overflow-hidden bg-black/30 backdrop-blur">
                    <img
                      src={mainPhotoURL}
                      alt="Asset"
                      className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => {
                        setImageModalUrl(mainPhotoURL);
                        setImageModalOpen(true);
                      }}
                    />
                    <button
                      onClick={() => setMainPhotoURL('')}
                      className="absolute top-2 right-2 bg-black/60 border border-white/30 text-white rounded-full p-1 hover:bg-black/80"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div className="w-48 h-48 border-2 border-dashed border-white/30 rounded-xl flex items-center justify-center bg-white/5 hover:bg-white/10 transition-colors backdrop-blur">
                    <label className="cursor-pointer text-center">
                      <div className="text-4xl mb-2">📷</div>
                      <div className="text-sm text-white/80">
                        {uploading ? t('assets.uploading') : t('assets.clickToUpload')}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleMainPhotoUpload}
                        disabled={uploading}
                        className="hidden"
                      />
                    </label>
                  </div>
                )}
                <p className="text-xs text-white/60 mt-1">
                  {t('assets.maxPhotoSize')}
                </p>
              </div>
            </div>
          </div>

            {/* Custom Fields Card */}
            <div className="bg-white/10 rounded-2xl border border-white/15 p-6 backdrop-blur-xl shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">{t('assets.customFields')}</h2>
                <div className="flex gap-2">
                  {FIELD_TYPES.map(ft => (
                    <button
                      key={ft.value}
                      onClick={() => addField(ft.value)}
                      className="px-3 py-1 text-sm border border-white/25 rounded bg-white/10 hover:bg-white/20 text-white shadow-sm backdrop-blur"
                      title={t('assets.addField', { type: t(`assets.field${ft.label}`) })}
                    >
                      {ft.icon} {t(`assets.field${ft.label}`)}
                    </button>
                  ))}
                </div>
              </div>

              {fields.length === 0 ? (
                <div className="text-center py-8 text-white/70">
                  {t('assets.noCustomFields')}
                </div>
              ) : (
                <div className="space-y-4">
                  {fields.map((field, index) => (
                    <FieldEditor
                      key={field.id}
                      field={field}
                      index={index}
                      totalFields={fields.length}
                      onUpdate={(updates) => updateField(field.id, updates)}
                      onDelete={() => deleteField(field.id)}
                      onMove={(direction) => moveField(index, direction)}
                      onFileUpload={(file) => handleFileFieldUpload(field.id, file)}
                      onToggleReminder={(reminder) => toggleReminder(field.id, reminder)}
                      onViewFile={(url, name) => {
                        setFileModalUrl(url);
                        setFileModalName(name);
                        setFileModalOpen(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

        </div>
      </div>

      {/* Modals */}
      <AssetFieldModals
        imageModalOpen={imageModalOpen}
        setImageModalOpen={setImageModalOpen}
        imageModalUrl={imageModalUrl}
        fileModalOpen={fileModalOpen}
        setFileModalOpen={setFileModalOpen}
        fileModalUrl={fileModalUrl}
        fileModalName={fileModalName}
      />
      </div>
    </div>
  );
}

function FieldEditor({ field, index, totalFields, onUpdate, onDelete, onMove, onFileUpload, onToggleReminder, onViewFile }) {
  const { t } = useTranslation();
  const REMINDER_OPTIONS = [
    { value: '1day', label: t('assets.oneDayBefore') },
    { value: '1week', label: t('assets.oneWeekBefore') },
    { value: '1month', label: t('assets.oneMonthBefore') },
    { value: '3months', label: t('assets.threeMonthsBefore') }
  ];

  return (
    <div className="border border-white/15 rounded-xl p-4 bg-white/5 backdrop-blur-xl shadow-lg">
      <div className="flex items-start gap-3">
        {/* Move buttons */}
        <div className="flex flex-col gap-1">
          <button
            onClick={() => onMove('up')}
            disabled={index === 0}
            className="text-white/60 hover:text-white disabled:opacity-30"
            title={t('assets.moveUp')}
          >
            ▲
          </button>
          <button
            onClick={() => onMove('down')}
            disabled={index === totalFields - 1}
            className="text-white/60 hover:text-white disabled:opacity-30"
            title={t('assets.moveDown')}
          >
            ▼
          </button>
        </div>

        {/* Field content */}
        <div className="flex-1 space-y-3">
          {/* Label */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-1">
              {t('assets.label')}
            </label>
            <input
              type="text"
              value={field.label}
              onChange={(e) => onUpdate({ label: e.target.value })}
              placeholder={t('assets.enterLabel', { type: field.type })}
              className="w-full border border-white/20 rounded-md px-3 py-2 bg-white/10 text-white placeholder-white/60 text-sm focus:outline-none focus:border-white/40 focus:ring-2 focus:ring-white/30"
            />
          </div>

          {/* Value based on type */}
          {field.type === 'text' && (
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                {t('assets.value')}
              </label>
              <input
                type="text"
                value={field.value}
                onChange={(e) => onUpdate({ value: e.target.value })}
                placeholder={t('assets.enterTextValue')}
                className="w-full border border-white/20 rounded-md px-3 py-2 bg-white/10 text-white placeholder-white/60 text-sm focus:outline-none focus:border-white/40 focus:ring-2 focus:ring-white/30"
              />
            </div>
          )}

          {field.type === 'number' && (
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                {t('assets.value')}
              </label>
              <input
                type="number"
                value={field.value}
                onChange={(e) => onUpdate({ value: e.target.value })}
                placeholder={t('assets.enterNumber')}
                className="w-full border border-white/20 rounded-md px-3 py-2 bg-white/10 text-white placeholder-white/60 text-sm focus:outline-none focus:border-white/40 focus:ring-2 focus:ring-white/30"
              />
            </div>
          )}

          {field.type === 'date' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">
                  {t('assets.date')}
                </label>
                <input
                  type="date"
                  value={field.value}
                  onChange={(e) => onUpdate({ value: e.target.value })}
                  className="w-full border border-white/20 rounded-md px-3 py-2 bg-white/10 text-white text-sm focus:outline-none focus:border-white/40 focus:ring-2 focus:ring-white/30"
                />
              </div>

              {/* Expiry settings */}
              <div className="bg-white/5 border border-white/15 rounded p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={field.enableExpiry || false}
                    onChange={(e) => onUpdate({ enableExpiry: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-white/80">
                    {t('assets.enableExpiry')}
                  </span>
                </label>

                {field.enableExpiry && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs text-white/70 mb-2">
                      {t('assets.sendReminders')}
                    </p>
                    {REMINDER_OPTIONS.map(reminder => (
                      <label key={reminder.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(field.expiryReminders || []).includes(reminder.value)}
                          onChange={() => onToggleReminder(reminder.value)}
                          className="rounded"
                        />
                        <span className="text-sm text-white/80">
                          {reminder.label}
                        </span>
                      </label>
                    ))}
                    
                    {/* Notification Recipients */}
                    <div className="mt-3 pt-3 border-t border-white/15">
                      <label className="block text-xs text-white/70 mb-1">
                        📧 {t('assets.notifyEmails')}
                      </label>
                      <textarea
                        value={field.notifyEmails || ''}
                        onChange={(e) => onUpdate({ notifyEmails: e.target.value })}
                        placeholder={t('assets.emailPlaceholder')}
                        rows={2}
                        className="w-full border border-white/20 rounded-md px-2 py-1 bg-white/10 text-white placeholder-white/60 text-xs focus:outline-none focus:border-white/40 focus:ring-2 focus:ring-white/30"
                      />
                      <p className="text-xs text-white/60 mt-1">
                        {t('assets.emailsHint')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {field.type === 'file' && (
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                {t('assets.file')}
              </label>
              {field.value ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onViewFile?.(field.value, field.fileName || t('assets.file'))}
                    className="text-cyan-200 hover:text-white text-sm flex-1 truncate text-left"
                  >
                    📎 {field.fileName || t('assets.viewFile')}
                  </button>
                  <button
                    onClick={() => onUpdate({ value: '', fileName: '' })}
                    className="text-rose-200 hover:text-rose-100 text-sm"
                  >
                    {t('assets.remove')}
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <div className="border-2 border-dashed border-white/25 rounded-md p-4 text-center hover:bg-white/10 bg-white/5 transition-colors">
                    <div className="text-sm text-white/80">
                      📎 {t('assets.clickToUploadFile')}
                    </div>
                  </div>
                  <input
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) onFileUpload(file);
                    }}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          )}

          {/* Show on mobile toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={field.showOnMobile || false}
              onChange={(e) => onUpdate({ showOnMobile: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm text-white/80">
              📱 {t('assets.showOnMobile')}
            </span>
          </label>
        </div>

        {/* Delete button */}
        <button
          onClick={onDelete}
          className="text-rose-300 hover:text-rose-200"
          title={t('assets.deleteField')}
        >
          🗑️
        </button>
      </div>
    </div>
  );
}

function AssetFieldModals({ imageModalOpen, setImageModalOpen, imageModalUrl, fileModalOpen, setFileModalOpen, fileModalUrl, fileModalName }) {
  return (
    <>
      {/* Image Viewer Modal */}
      {imageModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setImageModalOpen(false)}>
          <div className="relative max-w-4xl max-h-[90vh] p-4">
            <button
              onClick={() => setImageModalOpen(false)}
              className="absolute top-2 right-2 bg-white/90 text-slate-900 rounded-full w-10 h-10 flex items-center justify-center text-2xl hover:bg-white z-10 shadow-lg"
            >
              ✕
            </button>
            <img
              src={imageModalUrl}
              alt="Asset preview"
              className="max-w-full max-h-[85vh] object-contain rounded-xl border border-white/10 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* File Viewer Modal */}
      {fileModalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setFileModalOpen(false)}>
          <div className="bg-slate-900/90 border border-white/10 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 backdrop-blur-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">📎 {fileModalName}</h2>
                <button
                  onClick={() => setFileModalOpen(false)}
                  className="text-white/70 hover:text-white text-2xl"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-3">
                <a
                  href={fileModalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-cyan-300 to-blue-500 text-slate-900 font-semibold px-4 py-2 rounded text-center shadow-lg hover:from-cyan-200 hover:to-blue-400"
                >
                  🔗 Open in New Tab
                </a>
                <a
                  href={fileModalUrl}
                  download
                  className="block w-full bg-white/10 hover:bg-white/20 border border-white/15 text-white px-4 py-2 rounded text-center backdrop-blur"
                >
                  💾 Download File
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
